import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import Select from "react-select"

import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { useResetParamsOnTabChange } from "hooks/useResetParamsOnTabChange"
import { useVehicleOptions } from "hooks/stockHook"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useVehicleDailyAuditReport } from "hooks/useVehicleDailyAuditReport"
import { useCloseVehicleDailyAudit, useVehiclesByLastLocation } from "hooks/useVehicleDailyAudit"
import { formatReadableDate } from "utils/formatDate"
import { buildVehicleOptionLabel } from "helpers/stock_helpers"
import StockModelCell from "components/Common/stock-table/StockModelCell"
import MovementInfoCell from "components/Common/VehicleMovement/MovementInfoCell"
import MarkAddedButton from "components/Common/MarkAddedButton"

import DailyVehicleAuditHistory from "./DailyVehicleAuditHistory"

// Ids can come back as numbers or strings depending on the endpoint/env —
// never compare vehicle ids with === directly.
const sameId = (a, b) => String(a) === String(b)

const todayDate = () => new Date().toISOString().slice(0, 10)

const shiftDate = (dateStr, days) => {
  const [y, m, d] = dateStr.split("-").map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + days)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const AddVehicleToChecklist = ({ onAdd, excludeIds }) => {
  const [inputValue, setInputValue] = useState("")
  const [debouncedValue, setDebouncedValue] = useState("")

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(inputValue.trim()), 500)
    return () => clearTimeout(handler)
  }, [inputValue])

  const { data, isFetching } = useVehicleOptions({ identifier: debouncedValue }, !!debouncedValue)

  const options = useMemo(
    () =>
      (data?.data ?? [])
        .filter(item => !excludeIds.some(id => sameId(id, item.id)))
        .map(item => ({ label: buildVehicleOptionLabel(item), value: item.id, raw: item })),
    [data, excludeIds]
  )

  return (
    <Select
      options={options}
      value={null}
      isLoading={isFetching}
      onChange={selected => {
        if (!selected) return
        onAdd(selected.raw)
        setInputValue("")
        setDebouncedValue("")
      }}
      onInputChange={setInputValue}
      inputValue={inputValue}
      placeholder="Search vehicle ID / chassis to add..."
      noOptionsMessage={() => (debouncedValue ? "No vehicle found" : "Start typing to search")}
    />
  )
}

// Alternative way to build up the list: pull in everything last seen at a
// chosen location — previewed first so the user can drop any that shouldn't
// be marked present before they're actually added.
const AddVehiclesByLocation = ({ onAddMany, excludeIds }) => {
  const [locationId, setLocationId] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [removedIds, setRemovedIds] = useState([])
  const { locations } = useFilteredVehicleOptions()
  const { data, isFetching } = useVehiclesByLastLocation(locationId)

  const locationOptions = useMemo(
    () => (locations ?? []).map(location => ({ label: location.name, value: location.id })),
    [locations]
  )

  const fetchedVehicles = useMemo(
    () => (data?.data ?? []).filter(item => !excludeIds.some(id => sameId(id, item.id))),
    [data, excludeIds]
  )

  const previewVehicles = fetchedVehicles.filter(vehicle => !removedIds.some(id => sameId(id, vehicle.id)))

  const openPreview = () => {
    setRemovedIds([])
    setPreviewOpen(true)
  }

  const confirmAdd = () => {
    onAddMany(previewVehicles)
    setPreviewOpen(false)
    setLocationId(null)
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div style={{ minWidth: 240 }}>
          <Select
            options={locationOptions}
            value={locationOptions.find(o => o.value === locationId) ?? null}
            isLoading={isFetching}
            isClearable
            onChange={selected => setLocationId(selected?.value ?? null)}
            placeholder="...or add all last seen at a location"
          />
        </div>
        {locationId && (
          <Button color="light" size="sm" disabled={isFetching || fetchedVehicles.length === 0} onClick={openPreview}>
            {isFetching ? "Loading..." : `Preview (${fetchedVehicles.length})`}
          </Button>
        )}
      </div>

      <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="lg">
        <ModalHeader toggle={() => setPreviewOpen(false)}>Vehicles Last Seen at This Location</ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Review before adding — add one at a time, or remove any that shouldn't be marked present today.
          </p>
          <div className="table-responsive mb-0" style={{ maxHeight: 360, overflowY: "auto" }}>
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Vehicle</th>
                  <th>Last Movement</th>
                  <th style={{ width: 170 }} />
                </tr>
              </thead>
              <tbody>
                {previewVehicles.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-muted">Nothing left to add.</td></tr>
                ) : (
                  previewVehicles.map(vehicle => (
                    <tr key={vehicle.id}>
                      <td><StockModelCell stock={vehicle} isExpanded={() => false} toggleRow={() => {}} showIdentifiers /></td>
                      <td><MovementInfoCell data={vehicle.latest_movement} /></td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => {
                              onAddMany([vehicle])
                              setRemovedIds(prev => [...prev, vehicle.id])
                            }}
                          >
                            Add
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            className="text-danger"
                            onClick={() => setRemovedIds(prev => [...prev, vehicle.id])}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button color="dark" disabled={previewVehicles.length === 0} onClick={confirmAdd}>
            Add All {previewVehicles.length} Vehicle{previewVehicles.length === 1 ? "" : "s"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

const DailyVehicleAuditContainer = () => {
  useResetParamsOnTabChange("daily-audit")

  const { params, updateParams } = useListParams({
    tab: "daily-audit",
    date: todayDate(),
  })

  const [closeModal, setCloseModal] = useState(false)
  const [closeNotes, setCloseNotes] = useState("")
  const [checkedMap, setCheckedMap] = useState({})
  const [extraVehicles, setExtraVehicles] = useState([])
  const initKeyRef = useRef(null)

  const date = params.date || todayDate()
  const isFutureDate = date > todayDate()

  const queryParams = useMemo(() => ({ date }), [date])

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useVehicleDailyAuditReport(queryParams, true)
  const report = response?.data

  // Initialize the checklist's checked state from the server suggestion once
  // per date — subsequent refetches (e.g. window refocus) shouldn't stomp on
  // marks the user already made.
  useEffect(() => {
    if (!report) return
    if (initKeyRef.current === date) return
    initKeyRef.current = date

    const initial = {}
    report.checklist.forEach(row => {
      initial[row.vehicle.id] = true
    })
    setCheckedMap(initial)
    setExtraVehicles([])
  }, [report, date])

  const { mutateAsync: closeDayMutation, isPending: closingDay } = useCloseVehicleDailyAudit()

  const toggleVehicle = vehicleId => {
    setCheckedMap(prev => ({ ...prev, [vehicleId]: !prev[vehicleId] }))
  }

  const handleAddVehicle = vehicle => {
    setExtraVehicles(prev => [...prev, vehicle])
    setCheckedMap(prev => ({ ...prev, [vehicle.id]: true }))
  }

  const handleRemoveExtraVehicle = vehicleId => {
    setExtraVehicles(prev => prev.filter(v => !sameId(v.id, vehicleId)))
    setCheckedMap(prev => {
      const next = { ...prev }
      delete next[vehicleId]
      return next
    })
  }

  const checklistRows = report?.checklist ?? []
  const extraRows = extraVehicles.map(vehicle => ({
    vehicle,
    manually_added: true,
  }))
  const allRows = [...checklistRows, ...extraRows]
  const checkedCount = allRows.filter(row => checkedMap[row.vehicle.id]).length

  // Bulk-add path for "everything last seen at this location" — same
  // extra-row tracking as a single manual add, just for many at once.
  const handleAddVehicles = vehicles => {
    const newOnes = vehicles.filter(v => !allRows.some(row => sameId(row.vehicle.id, v.id)))
    if (newOnes.length === 0) return

    setExtraVehicles(prev => [...prev, ...newOnes])
    setCheckedMap(prev => {
      const next = { ...prev }
      newOnes.forEach(v => {
        next[v.id] = true
      })
      return next
    })
  }

  // Used from lists other than the checklist itself (e.g. Today's Movements)
  // — tracks the vehicle as an extra row the first time it's marked, then
  // behaves like a normal toggle.
  const toggleVehiclePresence = vehicle => {
    if (!vehicle) return
    const alreadyTracked = allRows.some(row => sameId(row.vehicle.id, vehicle.id))
    if (!alreadyTracked) {
      handleAddVehicle(vehicle)
      return
    }
    toggleVehicle(vehicle.id)
  }

  // Group check/uncheck for a given list of vehicles (works for both the
  // checklist and Today's Movements) — marking untracked vehicles present
  // also tracks them as extra rows, same as a single mark would.
  const setAllPresence = (vehicles, present) => {
    const validVehicles = vehicles.filter(Boolean)

    setCheckedMap(prev => {
      const next = { ...prev }
      validVehicles.forEach(vehicle => {
        next[vehicle.id] = present
      })
      return next
    })

    if (present) {
      setExtraVehicles(prev => {
        const tracked = [...checklistRows.map(row => row.vehicle), ...prev]
        const toAdd = []
        validVehicles.forEach(vehicle => {
          const isNew = ![...tracked, ...toAdd].some(v => sameId(v.id, vehicle.id))
          if (isNew) toAdd.push(vehicle)
        })
        return [...prev, ...toAdd]
      })
    }
  }

  const isAllChecked = vehicles => {
    const validVehicles = vehicles.filter(Boolean)
    return validVehicles.length > 0 && validVehicles.every(v => checkedMap[v.id])
  }

  const toggleAllPresence = vehicles => setAllPresence(vehicles, !isAllChecked(vehicles))

  const movementVehicles = (report?.same_day_movements ?? []).map(m => m.vehicle)
  const checklistVehicles = allRows.map(row => row.vehicle)

  // Checklist rows carry their own precomputed hint fields (current_location/
  // movement_type/movement_date/moved_by); manually-added rows (search or
  // add-by-location) carry the vehicle's real latest_movement relation
  // instead — normalize both into the shape MovementInfoCell expects.
  const rowMovementData = row => {
    if (row.manually_added) return row.vehicle?.latest_movement ?? null
    if (!row.current_location) return null

    return {
      movement_type: row.movement_type,
      location_to: row.current_location,
      movement_date: row.movement_date,
      movement_by: row.moved_by,
      mileage: null,
    }
  }

  // Preview of what the close will actually record, grouped the same way as
  // the printed audit: present / moved_out / missing. A vehicle from the
  // previous baseline that isn't checked today is "moved_out" if it has a
  // movement dated since that close, otherwise "missing" — approximating the
  // server's real classification (which runs authoritatively on save).
  const previousAuditDate = report?.previous_audit?.audit_date
  const presentRows = allRows.filter(row => checkedMap[row.vehicle.id])
  const goneChecklistRows = checklistRows.filter(row => !checkedMap[row.vehicle.id])
  const movedOutRows = goneChecklistRows.filter(
    row => row.movement_date && previousAuditDate && new Date(row.movement_date) > new Date(previousAuditDate)
  )
  const missingRows = goneChecklistRows.filter(row => !movedOutRows.includes(row))

  const closePreviewSections = [
    { key: "present", title: "Present", rows: presentRows, badge: "success" },
    { key: "moved_out", title: "Moved Out", rows: movedOutRows, badge: "primary" },
    { key: "missing", title: "Missing", rows: missingRows, badge: "danger" },
  ]

  const openCloseModal = () => {
    setCloseNotes("")
    setCloseModal(true)
  }

  const handleConfirmClose = async () => {
    try {
      const vehicleIds = allRows.filter(row => checkedMap[row.vehicle.id]).map(row => row.vehicle.id)
      const res = await closeDayMutation({
        audit_date: date,
        notes: closeNotes?.trim() || null,
        vehicle_ids: vehicleIds,
      })
      toast.success(res?.message ?? "Vehicle daily audit closed successfully.")
      setCloseModal(false)
      refetch()
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to close the day."
      toast.error(msg)
    }
  }

  return (
    <div>
      <Row className="g-2 align-items-end mb-3">
        <Col xs="auto">
          <Label className="mb-1 small fw-semibold d-block">Date</Label>
          <div className="d-flex gap-1">
            <Button color="light" size="sm" onClick={() => updateParams({ date: shiftDate(date, -1) })}>
              <i className="mdi mdi-chevron-left" />
            </Button>
            <Input
              type="date"
              bsSize="sm"
              value={date}
              onChange={e => updateParams({ date: e.target.value })}
            />
            <Button color="light" size="sm" onClick={() => updateParams({ date: shiftDate(date, 1) })}>
              <i className="mdi mdi-chevron-right" />
            </Button>
          </div>
        </Col>
        <Col xs="auto">
          <Button color="light" size="sm" onClick={() => refetch()}>
            <i className={`mdi mdi-refresh ${isFetching ? "mdi-spin" : ""}`} />
          </Button>
        </Col>
        <Col xs="auto" className="ms-auto d-flex gap-2 align-items-center">
          {report?.is_closed_today ? (
            <Badge color="dark" className="py-2 px-3">
              <i className="mdi mdi-lock-outline me-1" />
              Closed
            </Badge>
          ) : isFutureDate ? (
            <Badge color="warning" className="text-dark py-2 px-3">
              <i className="mdi mdi-calendar-alert me-1" />
              Future date — cannot close yet
            </Badge>
          ) : (
            <CanView access={[ACCESS.VEHICLE_MOVEMENT.ADD_DAILY_AUDIT]}>
              <Button color="dark" size="sm" onClick={openCloseModal} disabled={!report}>
                <i className="mdi mdi-lock-outline me-1" />
                Close Day ({checkedCount})
              </Button>
            </CanView>
          )}
        </Col>
      </Row>

      <div className="mb-3">
        <span className="badge bg-warning text-dark px-3 py-2" style={{ fontSize: "1rem" }}>
          <i className="mdi mdi-calendar-check me-1" />
          {formatReadableDate(date, "EEEE, MMMM dd, yyyy")}
        </span>
      </div>

      {isFutureDate && (
        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
          <i className="mdi mdi-information-outline me-1" />
          <strong>{formatReadableDate(date, "EEEE, MMMM dd, yyyy")}</strong> hasn't happened yet — a day can only be
          closed on or after that date, once you know which vehicles were actually present. Come back on that day
          to close it.
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : !report ? (
        <div className="text-center py-5 text-muted">No data available.</div>
      ) : (
        <>
          <Card className="border mb-3">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">
                  Today's Movements <Badge className="badge-soft-secondary">{report.same_day_movements.length}</Badge>
                </h5>
                <Button
                  color="light"
                  size="sm"
                  disabled={report.is_closed_today || movementVehicles.length === 0}
                  onClick={() => toggleAllPresence(movementVehicles)}
                >
                  {isAllChecked(movementVehicles) ? "Remove All" : "Add All"}
                </Button>
              </div>
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 140 }}>Added</th>
                      <th>Vehicle</th>
                      <th>Movement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.same_day_movements.length === 0 ? (
                      <tr><td colSpan={3} className="text-center text-muted">No movements recorded today.</td></tr>
                    ) : (
                      report.same_day_movements.map(movement => (
                        <tr key={movement.id}>
                          <td>
                            <MarkAddedButton
                              marked={!!checkedMap[movement.vehicle?.id]}
                              onToggle={() => toggleVehiclePresence(movement.vehicle)}
                              disabled={report.is_closed_today || !movement.vehicle}
                            />
                          </td>
                          <td><StockModelCell stock={movement.vehicle ?? {}} isExpanded={() => false} toggleRow={() => {}} showIdentifiers /></td>
                          <td><MovementInfoCell data={movement} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {report.is_closed_today ? (
            <Card className="border mb-3">
              <CardBody className="text-center text-muted py-4">
                This date is already closed. See Audit History below for details, or reopen it to make changes.
              </CardBody>
            </Card>
          ) : (
            <Card className="border mb-3">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="mb-0">
                    Mark Vehicles Present <Badge className="badge-soft-success">{checkedCount}</Badge>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    {report.previous_audit && (
                      <div className="text-muted small">
                        Previous close: {formatReadableDate(report.previous_audit.audit_date)}
                      </div>
                    )}
                    <Button
                      color="light"
                      size="sm"
                      disabled={checklistVehicles.length === 0}
                      onClick={() => toggleAllPresence(checklistVehicles)}
                    >
                      {isAllChecked(checklistVehicles) ? "Remove All" : "Add All"}
                    </Button>
                  </div>
                </div>

                <div className="mb-3 d-flex flex-wrap gap-3">
                  <div style={{ maxWidth: 420 }} className="flex-grow-1">
                    <AddVehicleToChecklist
                      onAdd={handleAddVehicle}
                      excludeIds={allRows.map(row => row.vehicle.id)}
                    />
                  </div>
                  <AddVehiclesByLocation
                    onAddMany={handleAddVehicles}
                    excludeIds={allRows.map(row => row.vehicle.id)}
                  />
                </div>

                <div className="table-responsive">
                  <Table bordered size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 140 }}>Added</th>
                        <th>Vehicle</th>
                        <th>Status</th>
                        <th>Last Movement</th>
                        <th style={{ width: 90 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {allRows.length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-muted">No vehicles yet — search above to add one.</td></tr>
                      ) : (
                        allRows.map(row => (
                          <tr key={row.vehicle.id}>
                            <td>
                              <MarkAddedButton
                                marked={!!checkedMap[row.vehicle.id]}
                                onToggle={() => toggleVehicle(row.vehicle.id)}
                              />
                            </td>
                            <td><StockModelCell stock={row.vehicle ?? {}} isExpanded={() => false} toggleRow={() => {}} showIdentifiers /></td>
                            <td>
                              {row.manually_added ? (
                                <Badge color="info">Added Manually</Badge>
                              ) : (
                                <Badge color="secondary">
                                  From Previous Close
                                  {report.previous_audit && ` (${formatReadableDate(report.previous_audit.audit_date)})`}
                                </Badge>
                              )}
                            </td>
                            <td><MovementInfoCell data={rowMovementData(row)} /></td>
                            <td>
                              {row.manually_added && (
                                <Button
                                  color="light"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => handleRemoveExtraVehicle(row.vehicle.id)}
                                >
                                  Remove
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      <div className="mt-4">
        <DailyVehicleAuditHistory />
      </div>

      <Modal isOpen={closeModal} toggle={() => setCloseModal(false)} size="lg">
        <ModalHeader toggle={() => setCloseModal(false)}>Confirm Close — {formatReadableDate(date)}</ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Preview of the audit record — same present / moved out / missing breakdown as the printed PDF. The
            server computes the final result when you close.
          </p>

          {closePreviewSections.map(section => (
            <div key={section.key} className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <strong>{section.title}</strong>
                <Badge color={section.badge}>{section.rows.length}</Badge>
              </div>
              <div className="table-responsive" style={{ maxHeight: 220, overflowY: "auto" }}>
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Vehicle</th>
                      <th>Last Movement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.length === 0 ? (
                      <tr><td colSpan={2} className="text-center text-muted">None.</td></tr>
                    ) : (
                      section.rows.map(row => (
                        <tr key={row.vehicle.id}>
                          <td><StockModelCell stock={row.vehicle ?? {}} isExpanded={() => false} toggleRow={() => {}} showIdentifiers /></td>
                          <td><MovementInfoCell data={rowMovementData(row)} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          ))}

          <Label htmlFor="close_notes">Notes (optional)</Label>
          <Input
            type="textarea"
            id="close_notes"
            value={closeNotes}
            onChange={e => setCloseNotes(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCloseModal(false)}>Cancel</Button>
          <Button color="dark" onClick={handleConfirmClose} disabled={closingDay || isFutureDate}>
            {closingDay ? "Closing..." : `Confirm Close (${checkedCount})`}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default DailyVehicleAuditContainer
